import { useEffect, useMemo, useState } from "react";
import { ArrowRight, BookOpen, CheckCircle2, XCircle } from "@/lib/icons";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { elements, type Element } from "@/data/elements";
import { reactivityData } from "@/data/elementDetails";
import MiniPeriodicTable, { type CellState } from "./MiniPeriodicTable";
import { getCategory, type GameCategoryKey } from "./categories";
import { RECAP_PROMPTS } from "./recapPrompts";
import { recordMissedElementBySymbol } from "@/learn/missedFromPlay";

export type RecapMistake = {
  player: 0 | 1;
  /** What the user typed. */
  attempt: string;
  /** Short, teacherly explanation. */
  explanation: string;
};

/**
 * Round-recap screen, shown between rounds. Designed to convert a "you
 * lost" moment into a teaching moment: shows what was named, what was
 * missed, why each mistake was wrong, and a quick formative MCQ.
 *
 * Reference: .lovable/element-battle-recap-mockup.png
 */
export default function RoundRecap({
  roundIndex,
  categoryKey,
  winnerName,
  loseReason,
  namedNumbers,
  mistakes,
  playerNames,
  onContinue,
  continueLabel,
}: {
  roundIndex: number;
  /** Only set in category mode - drives coverage + quick-check. */
  categoryKey?: GameCategoryKey;
  winnerName: string;
  loseReason?: string;
  namedNumbers: ReadonlySet<number>;
  mistakes: RecapMistake[];
  playerNames: [string, string];
  onContinue: () => void;
  continueLabel: string;
}) {
  const navigate = useNavigate();
  const cat = categoryKey ? getCategory(categoryKey) : null;

  // Build coverage state: pool members are named/missed, everyone else muted.
  const { poolElements, missed, states } = useMemo(() => {
    if (!cat) {
      return {
        poolElements: [] as Element[],
        missed: [] as Element[],
        states: new Map<number, CellState>(),
      };
    }
    const pool = elements.filter((e) => cat.matches.includes(e.category));
    const m = pool.filter((e) => !namedNumbers.has(e.number));
    const s = new Map<number, CellState>();
    for (const e of pool) {
      s.set(e.number, namedNumbers.has(e.number) ? "named" : "missed");
    }
    return { poolElements: pool, missed: m, states: s };
  }, [cat, namedNumbers]);

  const namedCount = poolElements.length - missed.length;
  const prompt = categoryKey ? RECAP_PROMPTS[categoryKey] : null;

  // Feed missed elements into the shared "Missed from Play" deck so the
  // Learn tab can quiz them later. Cap at 8 per round so a single bad
  // round doesn't flood the deck.
  useEffect(() => {
    for (const e of missed.slice(0, 8)) {
      recordMissedElementBySymbol(e.symbol, e.name, e.number);
    }
    // Run once per recap mount; missed list is stable for this round.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="rounded-xl border border-border bg-card p-5 mb-5 space-y-5">
      {/* Header */}
      <header>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Round {roundIndex} complete{cat ? ` - ${cat.label}` : ""}
        </p>
        <h2 className="text-xl font-semibold text-foreground mt-0.5">
          {winnerName} wins the round
        </h2>
        {loseReason && (
          <p className="text-sm text-muted-foreground mt-1">{loseReason}</p>
        )}
      </header>

      {/* Coverage (category mode only) */}
      {cat && (
        <section className="rounded-lg border border-border bg-background/40 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Coverage - {namedCount} of {poolElements.length} named
          </p>
          <MiniPeriodicTable
            states={states}
            ariaLabel={`Periodic table showing ${namedCount} named and ${missed.length} missed elements in the ${cat.label} category.`}
          />
          <div className="flex items-center gap-4 mt-2.5 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-primary/80 border border-primary inline-block" />
              Named ({namedCount})
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-destructive/15 border border-destructive/60 inline-block" />
              Missed ({missed.length})
            </span>
          </div>
        </section>
      )}

      {/* You missed */}
      {missed.length > 0 && (
        <section className="rounded-lg border border-border bg-background/40 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            You missed
          </p>
          <ul className="space-y-2.5">
            {missed.slice(0, 4).map((el) => (
              <MissedCard
                key={el.number}
                el={el}
                onPeriodic={() => navigate(`/?z=${el.number}`)}
                onMore={() => navigate(`/explore?z=${el.number}`)}
              />
            ))}
          </ul>
          {missed.length > 4 && (
            <p className="text-[11px] text-muted-foreground mt-2">
              + {missed.length - 4} more not shown
            </p>
          )}
        </section>
      )}

      {/* Mistakes worth reviewing */}
      {mistakes.length > 0 && (
        <section className="rounded-lg border border-border bg-background/40 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Mistakes worth reviewing
          </p>
          <ul className="space-y-1.5">
            {mistakes.map((m, i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 text-sm flex-wrap"
              >
                <Badge variant="outline" className="text-[10px] mt-0.5">
                  {playerNames[m.player]}
                </Badge>
                <Badge
                  variant="destructive"
                  className="text-[10px] font-mono mt-0.5"
                >
                  {m.attempt}
                </Badge>
                <span className="text-muted-foreground flex-1 min-w-[140px]">
                  {m.explanation}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Quick check */}
      {prompt && <QuickCheck prompt={prompt} />}

      {/* Footer actions */}
      <div className="flex flex-col-reverse sm:flex-row gap-2">
        <Button
          variant="outline"
          onClick={() => navigate("/explore")}
          className="flex-1"
        >
          <BookOpen size={14} className="mr-1.5" aria-hidden="true" />
          Review notes
        </Button>
        <Button onClick={onContinue} className="flex-1" size="lg">
          {continueLabel}
          <ArrowRight size={14} className="ml-1.5" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

function MissedCard({
  el,
  onPeriodic,
  onMore,
}: {
  el: Element;
  onPeriodic: () => void;
  onMore: () => void;
}) {
  const fact = factForElement(el);
  return (
    <li className="flex gap-3 p-2.5 rounded-md border border-border bg-card">
      <div className="flex-shrink-0 w-12 h-12 rounded-md bg-primary/15 border border-primary/40 flex flex-col items-center justify-center">
        <span className="text-[8px] font-mono text-primary leading-none">
          {el.number}
        </span>
        <span className="text-base font-bold font-mono text-primary leading-tight">
          {el.symbol}
        </span>
        <span className="text-[7px] text-primary/80 leading-none">
          {el.name}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">
          {el.name} <span className="text-muted-foreground font-normal"> -  {shortTag(el)}</span>
        </p>
        <p className="text-[12px] text-muted-foreground leading-relaxed mt-0.5">
          {fact}
        </p>
        <div className="flex gap-1.5 mt-1.5">
          <button
            type="button"
            onClick={onPeriodic}
            className="text-[11px] px-2 py-0.5 rounded border border-border bg-background hover:border-primary/40 transition-colors"
          >
            Periodic table
          </button>
          <button
            type="button"
            onClick={onMore}
            className="text-[11px] px-2 py-0.5 rounded border border-border bg-background hover:border-primary/40 transition-colors"
          >
            More on {el.symbol}
          </button>
        </div>
      </div>
    </li>
  );
}

function QuickCheck({
  prompt,
}: {
  prompt: { question: string; options: [string, string, string] };
}) {
  // Stable shuffled order; first option of `prompt.options` is correct.
  const [order] = useState(() => {
    const idx = [0, 1, 2].sort(() => Math.random() - 0.5);
    return idx;
  });
  const [picked, setPicked] = useState<number | null>(null);

  return (
    <section className="rounded-lg border border-border bg-background/40 p-4">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        Quick check
      </p>
      <p className="text-sm font-semibold text-foreground mb-2.5">
        {prompt.question}
      </p>
      <div className="space-y-1.5">
        {order.map((origIndex) => {
          const isCorrect = origIndex === 0;
          const isPicked = picked === origIndex;
          const showState = picked !== null;
          return (
            <button
              key={origIndex}
              type="button"
              onClick={() => picked === null && setPicked(origIndex)}
              disabled={picked !== null}
              className={`w-full text-left text-sm px-3 py-2 rounded-md border transition-colors flex items-center justify-between gap-2 ${
                showState && isCorrect
                  ? "border-primary/60 bg-primary/10"
                  : showState && isPicked
                  ? "border-destructive/60 bg-destructive/10"
                  : "border-border bg-background hover:border-primary/40"
              }`}
            >
              <span>{prompt.options[origIndex]}</span>
              {showState && isCorrect && (
                <CheckCircle2
                  size={14}
                  className="text-primary flex-shrink-0"
                  aria-label="Correct answer"
                />
              )}
              {showState && isPicked && !isCorrect && (
                <XCircle
                  size={14}
                  className="text-destructive flex-shrink-0"
                  aria-label="Incorrect"
                />
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ---- helpers ----

function shortTag(el: Element): string {
  switch (el.category) {
    case "noble-gas":
      return "noble gas";
    case "halogen":
      return "halogen";
    case "alkali-metal":
      return "alkali metal";
    case "alkaline-earth":
      return "alkaline earth metal";
    case "transition-metal":
      return "transition metal";
    case "metalloid":
      return "metalloid";
    case "nonmetal":
      return "nonmetal";
    case "lanthanide":
      return "lanthanide";
    case "actinide":
      return "actinide";
    default:
      return el.phase;
  }
}

/**
 * Best-effort one-sentence fact. Uses reactivity description when present;
 * falls back to a stock atomic-number / group line.
 */
function factForElement(el: Element): string {
  const r = reactivityData[el.number];
  if (r?.description) return r.description;
  const grp = el.group ? `group ${el.group}` : `period ${el.period}`;
  return `Atomic number ${el.number}, ${grp} - ${el.phase} at room temperature.`;
}
